import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';

import Button from '~/app/common/Button';
import Icon from '~/app/common/Icon';
import Loader from '~/app/common/Loader';
import Modal from '~/app/common/Modal';
import useTranslation from '~/app/hooks/useTranslation';
import { retryFailedSync } from '~/data/sync/drainOfflineQueue';
import { getSyncProgressState } from '~/data/sync/syncProgressState';
import { useSyncProgress } from '~/data/sync/useSyncProgress';

import { syncProgressItemLabel } from './syncProgressItemLabel';
import classes from './style.module.scss';
import {
  SyncProgressItem,
  SyncProgressItemStatus,
  SyncProgressStatus,
} from '../syncProgressTypes';

const TITLE_ID = 'sync-progress-dialog-title';
const SUBTITLE_ID = 'sync-progress-dialog-subtitle';
const SUCCESS_AUTO_CLOSE_MS = 3000;

export type SyncProgressDialogProps = {
  open: boolean;
  onClose: () => void;
};

export type SyncProgressDialogComponent = React.FC<SyncProgressDialogProps>;

function countByStatus(items: SyncProgressItem[], status: SyncProgressItemStatus): number {
  return items.filter((item) => item.status === status).length;
}

function progressPercent(completed: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  return Math.min(100, Math.round((completed / total) * 100));
}

const SyncProgressDialog: SyncProgressDialogComponent = ({ open, onClose }) => {
  const { t } = useTranslation('offline.syncProgress');
  const { status, total, completed, items } = useSyncProgress();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const visible = open && status !== 'idle';

  const syncedCount = useMemo(() => countByStatus(items, 'done'), [items]);
  const failedCount = useMemo(() => countByStatus(items, 'failed'), [items]);

  const percent = progressPercent(completed, total);

  const listItems = useMemo(() => {
    if (status === 'success') {
      return [];
    }
    if (status === 'partial_failure') {
      return items.filter((item) => item.status === 'failed');
    }
    return items;
  }, [items, status]);

  const liveMessage = useMemo(() => {
    switch (status) {
      case 'syncing':
        return t('ariaLiveSyncing', { completed, total });
      case 'paused':
        return t('ariaLivePaused');
      case 'success':
        return t('ariaLiveSuccess');
      case 'partial_failure':
        return t(
          failedCount === 1 ? 'ariaLiveNeedsAttention_one' : 'ariaLiveNeedsAttention_other',
          { count: failedCount }
        );
      default:
        return '';
    }
  }, [completed, failedCount, status, t, total]);

  useEffect(() => {
    if (!visible) {
      return undefined;
    }
    previousFocusRef.current = document.activeElement as HTMLElement;
    const frame = requestAnimationFrame(() => {
      dialogRef.current?.focus();
    });
    return () => {
      cancelAnimationFrame(frame);
      previousFocusRef.current?.focus?.();
    };
  }, [visible]);

  useEffect(() => {
    if (!visible || status !== 'success') {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      if (getSyncProgressState().status === 'success') {
        onClose();
      }
    }, SUCCESS_AUTO_CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [onClose, status, visible]);

  const handleRetry = useCallback(() => {
    void retryFailedSync();
  }, []);

  const itemStatusText = useCallback(
    (itemStatus: SyncProgressItemStatus): string => {
      switch (itemStatus) {
        case 'done':
          return t('itemStatusDone');
        case 'uploading':
          return t('itemStatusUploading');
        case 'waiting':
          return t('itemStatusWaiting');
        case 'failed':
          return t('itemStatusFailed');
      }
    },
    [t]
  );

  const renderStatusIcon = (itemStatus: SyncProgressItemStatus): React.ReactNode => {
    switch (itemStatus) {
      case 'done':
        return (
          <span className={classNames(classes.itemIcon, classes['itemIcon--done'])} aria-hidden>
            <Icon name="check" size="20" />
          </span>
        );
      case 'uploading':
        return (
          <span className={classes.itemIcon} aria-hidden>
            <Loader size="20" inline />
          </span>
        );
      case 'failed':
        return (
          <span className={classNames(classes.itemIcon, classes['itemIcon--failed'])} aria-hidden>
            <Icon name="warning" size="20" />
          </span>
        );
      case 'waiting':
      default:
        return (
          <span className={classes.itemIcon} aria-hidden>
            <Icon name="schedule" size="20" />
          </span>
        );
    }
  };

  const renderHeaderIcon = (dialogStatus: SyncProgressStatus): React.ReactNode => {
    switch (dialogStatus) {
      case 'syncing':
        return (
          <div className={classNames(classes.iconCircle, classes['iconCircle--accent'])}>
            <Icon name="loop" size="28" aria-hidden />
          </div>
        );
      case 'paused':
        return (
          <div className={classNames(classes.iconCircle, classes['iconCircle--neutral'])}>
            <Icon name="wifi_off" size="28" aria-hidden />
          </div>
        );
      case 'success':
        return (
          <div className={classNames(classes.iconCircle, classes['iconCircle--success'])}>
            <Icon name="check_circle" size="28" aria-hidden />
          </div>
        );
      case 'partial_failure':
        return (
          <div className={classNames(classes.iconCircle, classes['iconCircle--warning'])}>
            <Icon name="warning" size="28" aria-hidden />
          </div>
        );
      default:
        return null;
    }
  };

  const title = useMemo(() => {
    switch (status) {
      case 'syncing':
        return t('titleSyncing');
      case 'paused':
        return t('titlePaused');
      case 'success':
        return t('titleSuccess');
      case 'partial_failure':
        return t('titleNeedsAttention', { synced: syncedCount, failed: failedCount });
      default:
        return '';
    }
  }, [failedCount, status, syncedCount, t]);

  const subtitle = useMemo(() => {
    switch (status) {
      case 'syncing':
        return t('subtitleSyncing');
      case 'paused':
        return t('subtitlePaused');
      case 'success':
        return t(total === 1 ? 'subtitleSuccess_one' : 'subtitleSuccess_other', { count: total });
      case 'partial_failure':
        return t('subtitleNeedsAttention');
      default:
        return '';
    }
  }, [status, t, total]);

  if (!visible) {
    return null;
  }

  return (
    <Modal open={visible} onClose={onClose} position="center" className={classes.dialog}>
      <div
        ref={dialogRef}
        className={classes.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        aria-describedby={SUBTITLE_ID}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <p className={classes.srOnly} aria-live="polite" aria-atomic="true">
          {liveMessage}
        </p>

        <header className={classes.header}>
          {renderHeaderIcon(status)}
          <h2 id={TITLE_ID} className={classes.title}>{title}</h2>
          <p id={SUBTITLE_ID} className={classes.subtitle}>{subtitle}</p>
        </header>

        {status === 'syncing' && (
          <div className={classes.progressSection}>
            <div className={classes.progressMeta}>
              <span>
                {t(total === 1 ? 'progressLabel_one' : 'progressLabel_other', { completed, total })}
              </span>
              <span>{t('progressPercent', { percent })}</span>
            </div>
            <div
              className={classes.progressTrack}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percent}
              aria-label={t(
                total === 1 ? 'progressLabel_one' : 'progressLabel_other',
                { completed, total }
              )}
            >
              <div className={classes.progressFill} style={{ width: `${percent}%` }} />
            </div>
          </div>
        )}

        {listItems.length > 0 && (
          <ul className={classes.list}>
            {listItems.map((item) => (
              <li key={item.id} className={classes.listItem}>
                {renderStatusIcon(item.status)}
                <div className={classes.itemBody}>
                  <span className={classes.itemName}>{syncProgressItemLabel(item, t)}</span>
                  <span className={classes.itemStatus}>{itemStatusText(item.status)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}

        <footer className={classes.footer}>
          {status === 'syncing' && (
            <Button block className={classes.actionBtn} onClick={onClose}>
              {t('continueInBackground')}
            </Button>
          )}
          {status === 'paused' && (
            <Button block className={classes.actionBtn} onClick={onClose}>
              {t('close')}
            </Button>
          )}
          {status === 'success' && (
            <Button block color="primary" className={classes.actionBtn} onClick={onClose}>
              {t('done')}
            </Button>
          )}
          {status === 'partial_failure' && (
            <>
              <Button block color="primary" className={classes.actionBtn} onClick={handleRetry}>
                {t('retryNow')}
              </Button>
              <Button block className={classes.actionBtn} onClick={onClose}>
                {t('later')}
              </Button>
            </>
          )}
        </footer>
      </div>
    </Modal>
  );
};

export default SyncProgressDialog;
